import { QuantConfig, Case, SignalIntent } from 'v3/quantConfig/types/quantConfig.type';
import { Signal, Operand } from 'v3/quantConfig/types/signal.type';
import { Intent, QuickAdjustmentConfig } from 'v3/quantConfig/types/intent.type';
import { Pipe } from 'v3/quantConfig/types/pipe.type';
import {
  SignalPointer,
  OperandPointer,
  IntentPointer,
  AdjustmentIntentPointer,
  AdjustmentSignalPointer,
  EntrySignalPointer,
  EntryIntentPointer,
  ExitSignalPointer,
  PipeSignalPointer,
  PipeIntentPointer,
  QuickAdjustmentIntentPointer,
} from '../types/pointer';

type OperandHandler = (operand: Operand, pointer: OperandPointer) => Operand | void;
type SignalHandler = (signal: Signal, pointer: SignalPointer) => Signal | void;
type IntentHandler = (intent: Intent, pointer: IntentPointer) => Intent | void;
type QuickAdjustmentHandler = (
  quickAdjustment: QuickAdjustmentConfig,
  pointer: QuickAdjustmentIntentPointer
) => QuickAdjustmentConfig | void;
type PipeHandler = (pipe: Pipe, pointer: PipeSignalPointer) => Pipe | void;

const PIPE_CONFIG_SIGNAL_TYPES = ['signal', 'when', 'anchor'];

interface Handlers {
  eachSignal?: SignalHandler;
  eachOperand?: OperandHandler;
  eachIntent?: IntentHandler;
  eachQuickAdjustment?: QuickAdjustmentHandler;
  eachPipe?: PipeHandler;
}

type IntentPointerWithIntentIndex =
  | Omit<AdjustmentIntentPointer, 'intentIndex'>
  | Omit<PipeIntentPointer, 'intentIndex'>
  | Omit<EntryIntentPointer, 'intentIndex'>
  | Omit<QuickAdjustmentIntentPointer, 'intentIndex'>;
const callIntents = (
  arg: Intent[],
  partialPointer: IntentPointerWithIntentIndex,
  handler?: IntentHandler
): Intent[] => {
  if (handler!) {
    return arg.map(
      (intent: Intent, intentIndex) =>
        handler(intent, {
          ...partialPointer!,
          intentIndex,
          intentLabel: intent.identifier.label,
          intentType: intent.type,
        }) || intent
    );
  }
  return arg;
};

const callSignal = (arg: Signal, pointer: SignalPointer, handler?: SignalHandler): Signal => {
  if (handler!) {
    return handler(arg, pointer!) || arg;
  }
  return arg;
};

const callOperand = (arg: Operand, pointer: OperandPointer, handler?: OperandHandler): Operand => {
  if (handler!) {
    return handler(arg, pointer!) || arg;
  }
  return arg;
};

export const quantConfigIterator = (quantConfig: QuantConfig, handlers: Handlers): QuantConfig => {
  const newQuantConfig = { ...quantConfig };
  const {
    eachSignal: eachSignalRaw,
    eachOperand,
    eachIntent: eachIntentRaw,
    eachQuickAdjustment,
    eachPipe,
  } = handlers;

  const eachSignal: SignalHandler | undefined = eachOperand
    ? (signal: Signal, signalPointer: SignalPointer) => {
        const newSignal = callSignal(signal, signalPointer, eachSignalRaw);
        return newSignal?.map((condition: Signal[number], conditionIndex: number) => {
          const { leftOperand, rightOperand } = condition;
          const operandPointer: OperandPointer = {
            ...signalPointer,
            conditionIndex: conditionIndex,
          } as OperandPointer;
          return {
            ...condition,
            leftOperand: leftOperand && callOperand(leftOperand, operandPointer, eachOperand),
            rightOperand: rightOperand && callOperand(rightOperand, operandPointer, eachOperand),
          };
        });
      }
    : eachSignalRaw;

  const wrappedEachIntent = (intent: Intent, intentPointer: IntentPointer): Intent => {
    const newIntent = callIntents([intent], intentPointer, eachIntentRaw)[0];
    const { uiConfigs: { quickAdjustments = [] } = {} } = newIntent;
    const newQuickAdjustments = quickAdjustments?.map(
      (quickAdjustment: QuickAdjustmentConfig, quickAdjustmentIndex: number): QuickAdjustmentConfig => {
        const quickAdjustmentPointer = {
          ...intentPointer,
          type: 'quickAdjustmentIntentPointer',
          quickAdjustmentIndex,
        } as QuickAdjustmentIntentPointer;
        return eachQuickAdjustment!(quickAdjustment, quickAdjustmentPointer) || quickAdjustment;
      }
    );
    return {
      ...newIntent,
      uiConfigs: {
        ...newIntent.uiConfigs,
        quickAdjustments: newQuickAdjustments,
      },
    };
  };

  const eachIntent: IntentHandler | undefined = eachQuickAdjustment ? wrappedEachIntent : eachIntentRaw;

  newQuantConfig.cases = newQuantConfig.cases?.map((kase: Case, caseIndex) => {
    const { entry, exitSignal, adjustments } = kase;
    const casePointer = { caseIndex: caseIndex };
    const entrySignalPointer = { ...casePointer, type: 'entrySignalPointer' } as EntrySignalPointer;
    const exitSignalPointer = { ...casePointer, type: 'exitSignalPointer' } as ExitSignalPointer;
    const entryIntentPointer = { ...casePointer, type: 'entryIntentPointer' } as Omit<
      EntryIntentPointer,
      'intentIndex'
    >;
    return {
      ...kase,
      entry: {
        ...entry,
        signal: callSignal(entry.signal, entrySignalPointer, eachSignal),
        intents: callIntents(entry.intents, entryIntentPointer, eachIntent),
      },
      exitSignal: callSignal(exitSignal, exitSignalPointer, eachSignal),
      adjustments: adjustments?.map((adjustment: SignalIntent, adjustmentIndex: number) => {
        const adjustmentPointer = { ...casePointer, adjustmentIndex };
        const adjustmentIntentPointer = {
          ...adjustmentPointer,
          type: 'adjustmentIntentPointer',
        } as Omit<AdjustmentIntentPointer, 'intentIndex'>;
        const adjustmentSignalPointer = {
          ...adjustmentPointer,
          type: 'adjustmentSignalPointer',
        } as AdjustmentSignalPointer;

        return {
          ...adjustment,
          signal: callSignal(adjustment.signal, adjustmentSignalPointer, eachSignal),
          intents: callIntents(adjustment.intents, adjustmentIntentPointer, eachIntent),
        };
      }),
    };
  });

  newQuantConfig.pipeConfigs = newQuantConfig.pipeConfigs?.map((pipe: Pipe, pipeIndex: number) => {
    const newPipe: Pipe = { ...pipe, config: { ...pipe.config } };
    const { name, type, config } = newPipe;
    const pipeSignalPointer = {
      type: 'pipeSignalPointer',
      pipeIndex,
      pipeName: name,
      pipeType: type,
    } as PipeSignalPointer;
    const pipeIntentPointer = {
      type: 'pipeIntentPointer',
      pipeIndex,
      pipeName: name,
      pipeType: type,
    } as Omit<PipeIntentPointer, 'intentIndex'>;

    PIPE_CONFIG_SIGNAL_TYPES.forEach((key) => {
      const signal: Signal | undefined = config[key];
      if (signal) config[key] = callSignal(signal, pipeSignalPointer, eachSignal);
    });
    const legs: Intent[] | undefined = config?.legs;
    if (legs) config.legs = callIntents(legs, pipeIntentPointer, eachIntent);

    return eachPipe ? eachPipe(newPipe, pipeSignalPointer) || newPipe : newPipe;
  });

  return newQuantConfig;
};
